import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../stores';
import {
    ackOutlookPromo,
    fetchOutlookPromo,
    OutlookPromoAction,
    OutlookPromoResponse,
    OutlookPromoTrigger,
} from '../services/api/outlookPromoService';
import { ADMIN_ONBOARDING_PENDING_KEY } from '../utils/adminOnboardingStorage';

const sessionAckKey = (userId: string, trigger: OutlookPromoTrigger) =>
    `outlook_promo_ack_${trigger}_${userId}`;

const modalSeenKey = (userId: string, trigger: OutlookPromoTrigger) =>
    `outlook_promo_modal_${userId}_${trigger}`;

export function useOutlookPromo(options?: { autoFetch?: boolean }) {
    const accessToken = useAppSelector((s) => s.auth.access_token);
    const userId = useAppSelector((s) => s.auth.session?.user?._id || s.auth.user?._id);
    const role = useAppSelector((s) => s.role.role);
    const [data, setData] = useState<OutlookPromoResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const fetchedRef = useRef(false);

    const refresh = useCallback(async () => {
        if (!accessToken) return null;
        setLoading(true);
        try {
            const next = await fetchOutlookPromo(accessToken);
            setData(next);
            return next;
        } finally {
            setLoading(false);
        }
    }, [accessToken]);

    useEffect(() => {
        if (options?.autoFetch === false) return;
        if (!accessToken || fetchedRef.current) return;
        fetchedRef.current = true;
        void refresh();
    }, [accessToken, options?.autoFetch, refresh]);

    const ack = useCallback(
        async (trigger: OutlookPromoTrigger, action: OutlookPromoAction) => {
            if (!accessToken || !userId) return null;
            try {
                sessionStorage.setItem(sessionAckKey(String(userId), trigger), action);
                if (action === 'shown' || action === 'dismiss' || action === 'cta') {
                    localStorage.setItem(modalSeenKey(String(userId), trigger), 'true');
                }
            } catch {
                /* ignore */
            }
            const next = await ackOutlookPromo(accessToken, trigger, action);
            setData(next);
            return next;
        },
        [accessToken, userId],
    );

    const shouldShow = useCallback(
        (trigger: OutlookPromoTrigger) => {
            if (!data?.eligible) return false;
            if (!data.pending?.includes(trigger)) return false;
            if (!userId) return false;

            // Ne pas empiler T2 pendant le funnel onboarding admin
            if (
                trigger === 'first_mapping' &&
                typeof sessionStorage !== 'undefined' &&
                sessionStorage.getItem(ADMIN_ONBOARDING_PENDING_KEY) === 'true'
            ) {
                return false;
            }

            // Priorité WhatsNew : ne pas ouvrir Outlook T2 si une annonce est encore pending
            if (trigger === 'first_mapping' && typeof sessionStorage !== 'undefined') {
                try {
                    const raw = sessionStorage.getItem('pending_announcements');
                    const pending = raw ? (JSON.parse(raw) as string[]) : [];
                    if (Array.isArray(pending) && pending.length > 0) {
                        return false;
                    }
                } catch {
                    /* ignore */
                }
            }

            const roleUpper = String(role || '').toUpperCase();
            if ((trigger === 'onboarding' || trigger === 'first_mapping') && roleUpper !== 'ADMIN') {
                return false;
            }
            if (trigger === 'first_interview' && roleUpper === 'ADMIN') {
                return false;
            }

            try {
                if (localStorage.getItem(modalSeenKey(String(userId), trigger)) === 'true') {
                    return false;
                }
            } catch {
                /* ignore */
            }
            return true;
        },
        [data, role, userId],
    );

    return {
        data,
        loading,
        refresh,
        ack,
        shouldShow,
        installUrl: data?.install_url || 'https://appsource.microsoft.com/',
        eligible: Boolean(data?.eligible),
    };
}
